import { defineStore } from 'pinia';

export function createStore<T>(StoreClass: new () => T) {
  const instance: any = new StoreClass();

  // Walk the prototype splitting methods (registered as Pinia actions)
  // from `get` accessors (registered as Pinia getters). Without the
  // getter branch, `public get foo()` on the class would be silently
  // dropped — the property would appear in the type signature but
  // resolve to `undefined` at runtime, corrupting any caller that
  // reads it.
  const actions: any = {};
  const getters: any = {};
  for (const propName of Object.getOwnPropertyNames(StoreClass.prototype)) {
    if (propName === 'constructor') {
      continue;
    }
    const descriptor = Object.getOwnPropertyDescriptor(StoreClass.prototype, propName);
    if (descriptor?.get) {
      // Pinia binds `this` of a getter to the store, which carries the
      // state + other getters. The class accessor expects the same
      // shape, so a direct `.call(this)` forwards correctly.
      getters[propName] = function (this: any) {
        return descriptor.get!.call(this);
      };
    } else if (typeof instance[propName] === 'function') {
      actions[propName] = function (this: any, ...args: any[]) {
        return instance[propName].apply(this, args);
      };
    }
  }

  const state = () =>
    Object.getOwnPropertyNames(instance).reduce((acc: any, propName) => {
      acc[propName] = instance[propName];

      return acc;
    }, {});

  return defineStore(StoreClass.name, {
    state,
    actions,
    getters,
  }) as () => T;
}
